<template>
	<form
		:class="[
			'styled-form',
			'styled-form--white-box',
			{'is-loading': isLoading}
		]"
		@submit.prevent="onSubmit"
	>

		<div v-if="isLoading" class="styled-form__overlay">
			<div class="preloader"></div>
		</div>

		<global-message v-if="errors.global" :value="errors.global"></global-message>

		<div class="styled-form__header">
			<h2 class="styled-form__title">Create new password</h2>
			<h3 class="styled-form__subtitle">Please enter your verification code and new password below.</h3>
		</div>

		<div class="styled-form__corset">

			<form-input
				v-model="data.email"
				name="email"
				label="Email Address *"
				:error="errors.email"
				v-if="showEmail"
				class="to-lower-case"
			></form-input>

			<form-input
				v-model="data.code"
				name="code"
				type="number"
				label="Code *"
				:error="errors.code"
				v-if="showCode"
				:max=10000000
			></form-input>

			<form-input
				v-model="data.newPassword"
				type="password"
				name="newPassword"
				label="New password"
				:error="errors.newPassword"
				@keydown="passwordPolicyInline"
			/>

			<form-input
				v-model="data.confirmNewPassword"
				type="password"
				name="confirmNewPassword"
				label="Confirm new password"
				:error="errors.confirmNewPassword"
				@keydown="passwordMatchInline"
			/>

		</div>

		<div class="styled-form__v-rhythm text--center">
			<button
				class="btn btn--secondary"
				:disabled="isLoading"
				type="button"
				@click.prevent="cancel"
			>Cancel</button>
			<button
				class="btn"
				type="submit"
				:disabled="isLoading"
			>Continue</button>
		</div>

		<div class="styled-form__corset" v-if="sendAgain">
			<div class="styled-form__info styled-form__info--bg text--center">
				Haven’t received the email?
				<a
					class="styled-form__link styled-form__link--lowercase"
					:disabled="isLoading"
					@click.prevent="sendAgain"
				>Send again</a>
			</div>
		</div>

	</form>
</template>

<style>
	.styled-form__header {
		margin-bottom: 20px;
	}

</style>

<script>
    import { mapState } from 'vuex';
    import GlobalMessage from '../messages/GlobalMessage.vue';
    import isEmail from '../../helpers/isEmail';
    import FormInput from '../formFields/FormInput.vue';

    export default {
        components: {
            FormInput,
            GlobalMessage
        },
        props: {
            submit: {
                type: Function,
                required: true
            },
            cancel: {
                type: Function,
                required: true
            },
            sendAgain: {
                type: Function
            },
            email: String,
            code: String
        },
        data() {
            return {
                isLoading: false,
                data: {
                    email: '',
                    code: '',
                    newPassword: '',
                    confirmNewPassword: ''
                },
                errors: {},
                showEmail: true,
                showCode: true
            }
        },
        created() {
            this.validateProps();
        },
        methods: {
            validateProps() {
                if (isEmail(this.email)) {
					let emailProp = this.email;
					this.data.email = emailProp.toLowerCase();
                    this.showEmail = false;
                }

                if (this.code && /^\d+$/.test(this.code)) {
                    this.data.code = this.code;
                    this.showCode = false;
                }
            },
            onSubmit() {
                this.errors = this.validate(this.data);

                if (Object.keys(this.errors).length === 0) {
                    this.isLoading = true;
                    this.data.email = this.data.email.toLowerCase();
                    this.submit(this.data).catch(errors => {
                        this.isLoading = false;
                        this.errors = errors;

                        if (errors.global == 'Invalid verification code provided, please try again.') {
                            this.showCode = true;
                            errors.code = 'Invalid verification code provided';
                        }

                        if (errors instanceof Error) {
                            // console.error(errors);
                        }
                    });
                }
            },
            validate(data)
            {
                const errors = {};

                if (!data.email.length) {
                    errors.email = 'This field is required';
                } else if (!isEmail(data.email)) {
                    errors.email = 'Invalid email';
                }

                if (!data.code.length) {
                    errors.code = 'This field is required';
                } else if (!(/^\d+$/.test(data.code))) {
                    errors.code = 'This field can only contain numeric values';
                }

                let passwordErrors = this.validatePassword(data.newPassword);
                if(passwordErrors.length){
                    errors.newPassword = this.passwordPolicyErrorFormat(passwordErrors);
                }

                let passwordMatch = this.validatePasswordMatch(data.newPassword, data.confirmNewPassword);
                if(passwordMatch != ""){
                    errors.confirmNewPassword = passwordMatch;
                }

                return errors;
            },

            validatePassword(password) {
                let errors = [];

                if (!password.length) {
                    errors.push('This field is required');
                }
                if (password.length < 8) {
                    errors.push('Password must be at least 8 characters');
                }
                if (!(/[a-z]/.test(password))) {
                    errors.push('Password must contain at least one lowercase letter');
                }
                if (!(/[A-Z]/.test(password))) {
                    errors.push('Password must contain at least one uppercase letter');
                }
                if (!(/[0-9]/.test(password))) {
                    errors.push('Password must contain at least one number');
                }
                /*
                if (!(/[!|@|#|$|%|^|&|*|(|)|-|_|]/.test(password))) {
                    errors.push('Password must contain at least one special character');
                }
                */

                return errors;
            },
            validatePasswordMatch(password, confirm){
                let errors = '';

                if(!confirm.length){
                    errors = 'This field is required';
                } else if(password !== confirm){
                    errors = 'The password you entered does not match';
                }
                return errors;
            },
            passwordPolicyInline() {
                this.$nextTick(() => {
                    if (this.data.newPassword.length > 0) {
                        let errors = this.validatePassword(this.data.newPassword);
                        if(errors.length){
                            this.errors.newPassword = this.passwordPolicyErrorFormat(errors);
                        }else{
                            delete this.errors.newPassword;
                        }
                    }else{
                        delete this.errors.newPassword;
                    }
                });
            },
            passwordPolicyErrorFormat(errors){
                let errorMsg = "<ul>";
                errors.forEach(function(item){
                    errorMsg = errorMsg + " <li> "+item+"</li>";
                });
                errorMsg = errorMsg + "</ul>";
                return errorMsg;
            },
            passwordMatchInline() {
                this.$nextTick(() => {
                    if (this.data.confirmNewPassword.length > 0) {
                        let errors = this.validatePasswordMatch(this.data.newPassword, this.data.confirmNewPassword);
                        if(errors !== "") {
                            this.errors.confirmNewPassword = errors;
                        }else{
                            delete this.errors.confirmNewPassword;
                        }
                    }else{
                        delete this.errors.confirmNewPassword;
                    }
                });
            }
        }
    };
</script>
